import {
  deriveMarketUi,
  getVirtualOutcomesForMonoCustom,
  withMonoCustomComplement,
} from '@/features/market/utils/marketType'
import { getOutcomeDisplayLabel, getPositionLabels } from '@/features/market/utils/positionLabels'
import { QUESTION_STATUS } from '@/constants/marketBackendEnums'

/**
 * Mirrors the colour list inside the market page's Chart, which assigns them by
 * series index. Kept here only for the legend — the chart itself colours its own
 * lines — so the dot beside a name matches the line drawn for it. Its third
 * colour flips with the theme there; Neutral-6 holds that same pair of values.
 */
const SERIES_COLORS = [
  '#33AC61',
  '#ED0071',
  'var(--color-Neutral-6)',
  '#F7931A',
  '#9966FF',
  '#0098FF',
  '#4C626D',
  '#342AB6',
  '#FF54A8',
]

/**
 * Rows the slide lists for a multi-outcome market, most likely first. The chart
 * is held to the same set, so the two always describe the same outcomes.
 */
const MAX_OUTCOMES = 3

/**
 * Prices are keyed by OOM or by the market's canonical currency, mirroring the
 * lookup the outcome list and the market chart already do.
 */
const priceKeyFor = (question, currencyId) => (currencyId === 'OOM' ? 'OOM' : question.canonical_currency)

const toPercentage = (outcome, priceKey) => Math.round(100 * (outcome?.price?.[priceKey] ?? 0))

/* `description` holds HTML; the panel shows a plain-text excerpt of it. */
export const toDetailsText = html =>
  (html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim()

const EMPTY_CHART = { prices: [], legend: [] }

const outcomeIndexFor = (question, serie, position) => {
  const outcomes = question?.outcomes
  if (!outcomes || outcomes.length < 2 || serie?.outcome_id == null) return position

  const index = outcomes.findIndex(outcome => outcome.id === serie.outcome_id)
  return index === -1 ? position : index
}

/**
 * Statuses under which a question or outcome no longer takes bets. `paused` is
 * left out: it is a temporary hold, not a settlement, so a paused outcome is
 * still the question the market asks.
 */
export const SETTLED_STATUSES = Object.freeze([
  QUESTION_STATUS.RESOLVED,
  QUESTION_STATUS.CANCELLED,
  QUESTION_STATUS.STOPPED,
  QUESTION_STATUS.REVERSED,
])

export const isSettledStatus = status => SETTLED_STATUSES.includes(status)

/** Reads the outcome's own `status` and `disabled` flag. */
const isOpenOutcome = outcome => !outcome?.disabled && !isSettledStatus(outcome?.status)

const toLongShortPair = (outcome, question, priceKey) => {
  const { longLabel, shortLabel } = getPositionLabels(outcome, question)
  const long = toPercentage(outcome, priceKey)

  return [
    { id: `${outcome.id}-long`, label: longLabel, probability: long, tone: 'up' },
    { id: `${outcome.id}-short`, label: shortLabel, probability: 100 - long, tone: 'down' },
  ]
}

/**
 * Follows the descriptor rather than re-reading the payload:
 *  - `buttons` (mono yes/no and up/down) becomes the long/short pair;
 *  - `needsVirtualOutcomes` (mono custom, e.g. Bulls vs Dogs) has one physical
 *    outcome carrying two custom labels, so it lists the two virtual rows the
 *    market page builds from the same helper;
 *  - everything else lists its open outcomes, most likely first. Settled
 *    outcomes only fill the list once nothing is left open. When a single
 *    outcome remains open it is read as a yes/no pair under its own title,
 *    since that is the one question the market still asks.
 *
 * Returns the rows, whether they read as a binary pair, the title the pair sits
 * under (single-open multi only) and the payload indexes of the listed outcomes
 * (null for mono markets, whose series are the two positions).
 */
const toOutcomes = (question, ui, priceKey) => {
  if (ui.displayMode === 'buttons') {
    return {
      outcomes: toLongShortPair(question.outcomes[0], question, priceKey),
      binary: true,
      focusTitle: null,
      outcomeIndexes: null,
    }
  }

  if (ui.needsVirtualOutcomes && question.outcomes.length === 1) {
    const { virtualOutcomes } = getVirtualOutcomesForMonoCustom(question)

    if (virtualOutcomes.length === 2) {
      return {
        /* The two virtual rows are the market's long and short sides, so they
           carry the same tones as a yes/no pair. */
        outcomes: virtualOutcomes.map((virtualOutcome, position) => ({
          id: virtualOutcome.virtualId,
          label: virtualOutcome.title,
          probability: toPercentage(virtualOutcome, priceKey),
          tone: position === 0 ? 'up' : 'down',
        })),
        binary: true,
        focusTitle: null,
        outcomeIndexes: null,
      }
    }
  }

  /* The payload index is carried through the sort so the chart can pick out the
     same outcomes; the series come back in payload order, not this one. */
  const indexed = question.outcomes.map((outcome, index) => ({ outcome, index }))
  const open = indexed.filter(entry => isOpenOutcome(entry.outcome))

  if (open.length === 1 && indexed.length > 1) {
    const [{ outcome, index }] = open

    return {
      outcomes: toLongShortPair(outcome, question, priceKey),
      binary: true,
      focusTitle: outcome.title,
      outcomeIndexes: [index],
    }
  }

  const listed = (open.length ? open : indexed)
    .map(({ outcome, index }) => ({
      id: outcome.id,
      index,
      label: outcome.title,
      probability: toPercentage(outcome, priceKey),
    }))
    .sort((a, b) => b.probability - a.probability)
    .slice(0, MAX_OUTCOMES)

  return {
    outcomes: listed,
    binary: false,
    focusTitle: null,
    outcomeIndexes: ui.isMono ? null : listed.map(outcome => outcome.index),
  }
}

/**
 * Adapts a market from the feed to the shape the carousel reads. The chart is
 * not part of it: price history is a separate request, handed to `toChartData`
 * once it lands.
 *
 * @param {Object} question - market as the feed returns it
 * @param {{currencyId: string}} options - the active currency, picking the price key
 * @returns {Object|null} null when the market carries nothing to show
 */
export const toFeaturedMarket = (question, { currencyId } = {}) => {
  if (!question?.id || !question?.outcomes?.length) return null

  const ui = deriveMarketUi(question)
  const priceKey = priceKeyFor(question, currencyId)
  const isRealMoney = currencyId !== 'OOM'

  const { outcomes, binary, focusTitle, outcomeIndexes } = toOutcomes(question, ui, priceKey)

  return {
    id: question.id,
    slug: question.slug,
    /* Kept so the carousel can reach the market helpers (chart labels, detail
       fetch) instead of this module having to mirror what they already do. */
    question,
    title: question.title,
    volume: (isRealMoney ? question.volume_real_money : question.volume_play_money) || 0,
    binary,
    /* The one outcome still open in a multi-outcome market, whose yes/no pair
       `outcomes` holds; null everywhere else. */
    focusTitle,
    details: toDetailsText(question.description),
    outcomes,
    outcomeIndexes,
  }
}

/**
 * Prepares a `price_history` response for the market page's chart. The series
 * are left in the shape that chart already reads — it does its own
 * interpolation and axis work — and are only narrowed to the outcomes the slide
 * lists. The legend is derived alongside, since the chart draws lines only.
 *
 * @param {Array} prices - series as the endpoint returns them ({ name, data: [{x, y}] })
 * @param {{question: Object, outcomeIndexes: number[]|null}} options - the market
 *   the series belong to, and which of its outcomes the slide lists
 */
export const toChartData = (prices, { question, outcomeIndexes = null } = {}) => {
  /* Mono-custom markets (e.g. Bulls vs Dogs) come back as a single series; the
     complementary side is synthesized here so both sides are drawn, exactly as
     the market page's chart does before handing its data over. */
  const withComplement = withMonoCustomComplement(prices, question)
  const all = (withComplement || []).filter(serie => serie?.data?.length)

  /* One line per outcome the slide lists — the same set, not the first few in
     payload order, which the list's sort by probability would not match. A
     series is only present for an outcome that has traded, so its position in
     the response is not its outcome's index in the payload; the index the
     filter and the legend read comes from `outcome_id`. Mono markets are left
     on position, where the two series share the single outcome and stand for
     its long and short sides. */
  const kept = all
    .map((serie, position) => ({ serie, index: outcomeIndexFor(question, serie, position) }))
    .filter(entry => !outcomeIndexes || outcomeIndexes.includes(entry.index))

  if (!kept.length) return EMPTY_CHART

  const legend = kept.map(({ serie, index }, position) => ({
    id: serie.outcome_id != null ? `${serie.outcome_id}-${index}` : `serie-${index}`,
    label: getOutcomeDisplayLabel(question, serie, index, all.length),
    color: SERIES_COLORS[position % SERIES_COLORS.length],
    value: Math.round(serie.data[serie.data.length - 1].y),
  }))

  return { prices: kept.map(entry => entry.serie), legend }
}
